import { env } from "@/lib/env"
import type { CodeResponse, GroupedCodeValues } from "@/types/code-values"
import { requestTokenDueToAuthFailure } from "@/lib/app-messaging"
import { tokenManager } from "@/lib/token-manager"

// Maximum number of retry attempts
const MAX_RETRIES = 2

// Code service that handles fetching code values
export const codeService = {
  // Get raw code values response from the API
  getRawCodeValues: async (token: string | null): Promise<CodeResponse> => {
    console.log("=== Code Service Debug ===")
    console.log("Environment variables:")
    console.log("env.apiUrl:", env.apiUrl)
    console.log("env.buildEnv:", env.buildEnv)
    console.log("========================")

    console.log("getRawCodeValues called")

    // If no token is provided, throw an error
    if (!token) {
      throw new Error("Authentication token is required to fetch code values")
    }

    // Try to fetch code values from the API with retries
    try {
      const apiUrl = `${env.apiUrl}/api/code`
      console.log(`Fetching code values from ${apiUrl}`)

      const response = await fetchWithRetry<CodeResponse>(
        apiUrl,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
        MAX_RETRIES,
      )

      return response
    } catch (error) {
      console.error("Failed to fetch code values:", error)
      throw error
    }
  },

  // Get processed code values from the API
  getCodeValues: async (token: string | null): Promise<GroupedCodeValues> => {
    const response = await codeService.getRawCodeValues(token)

    // Group the items by groupName for easier access
    const groupedValues: GroupedCodeValues = {}

    if (response.Items && Array.isArray(response.Items)) {
      response.Items.forEach((item) => {
        if (!groupedValues[item.groupName]) {
          groupedValues[item.groupName] = []
        }
        groupedValues[item.groupName].push(item)
      })

      // Sort each group by sortOrder
      Object.keys(groupedValues).forEach((groupName) => {
        groupedValues[groupName].sort((a, b) => a.sortOrder - b.sortOrder)
      })
    }

    return groupedValues
  },
}

// Helper function to fetch with retry logic and token management
async function fetchWithRetry<T>(url: string, options: RequestInit, maxRetries: number): Promise<T> {
  let lastError: Error | null = null

  // Try the request up to maxRetries times
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      console.log(`Attempt ${attempt + 1}: Fetching ${url}`)
      const response = await fetch(url, options)

      if (!response.ok) {
        // Handle 401 Unauthorized errors by requesting a new token and queuing for retry
        if (response.status === 401) {
          console.warn("Received 401 Unauthorized in code service from", url)

          // Only request a new token if we haven't already requested one
          if (tokenManager.shouldRequestToken()) {
            console.log("Requesting new token from parent")
            requestTokenDueToAuthFailure(`401 Unauthorized from ${url}`)
          }

          // Queue this request to be retried when we get a new token
          console.log("Queueing code values request for retry when new token is received")
          const retriedResponse = await tokenManager.queueRequest(url, options)

          if (!retriedResponse.ok) {
            const errorText = await retriedResponse.text()
            console.error(`HTTP error ${retriedResponse.status}: ${retriedResponse.statusText}`, errorText)
            throw new Error(`HTTP error ${retriedResponse.status}: ${retriedResponse.statusText}`)
          }

          const data = await retriedResponse.json()
          console.log("Fetch successful after token retry, data:", data)
          return data
        }

        const errorText = await response.text()
        console.error(`HTTP error ${response.status}: ${response.statusText}`, errorText)
        throw new Error(`HTTP error ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error(`Attempt ${attempt + 1}/${maxRetries} failed:`, error)
      lastError = error instanceof Error ? error : new Error(String(error))

      // If this is not the last attempt, wait before retrying
      if (attempt < maxRetries - 1) {
        // Exponential backoff: wait longer between each retry
        const delay = Math.pow(2, attempt) * 500 // 500ms, 1000ms, 2000ms, etc.
        await new Promise((resolve) => setTimeout(resolve, delay))
      }
    }
  }

  // If we've exhausted all retries, throw the last error
  throw lastError || new Error("Failed to fetch after multiple attempts")
}
